import { useState } from "react";
import { styled, lighten } from "@mui/material/styles";
import {
  Paper,
  Grid,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  TextField,
  Typography,
  Box,
  AccordionProps,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummaryProps,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
} from "@mui/material";
import { useGiftCardCatalogContext } from "@/context/GiftCardCatalogContext";
import { BRAND_CATEGORIES_ALL, BRAND_CATEGORIES as CATEGORIES } from "@/const";
import Link from "@/components/link";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import FilterListIcon from "@mui/icons-material/FilterList";
import CardImage from "@/components/cardImage";
import { TangoBrand } from "@t3/shared";

const categoryList = Object.entries(CATEGORIES);

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

export const ShopGiftCards = () => {
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const { catalog, catalogLoading, catalogError } = useGiftCardCatalogContext();
  const [accordionOpen, setAccordionOpen] = useState(false);

  const toggleAccordion = () => {
    setAccordionOpen((prevOpen) => !prevOpen);
  };

  const handleChangeCategory = (e: any) => {
    setCategory(e.target.value);
  };

  const popularCardsAtTop = !search && category === BRAND_CATEGORIES_ALL;

  if (catalogLoading) {
    return <div>Loading...</div>;
  }

  if (catalogError) {
    throw catalogError;
  }

  const textIsInSearch = (text: string) => {
    return text.toLowerCase().includes(search.toLowerCase());
  };

  const renderCards = () => {
    const filteredCards = catalog.filter((card) => {
      // Filter by category
      if (
        category !== BRAND_CATEGORIES_ALL &&
        (!card.categories || !card.categories.includes(category as any)) //TODO: this looks broken, but I confirm it works???
      ) {
        return false;
      }

      // Filter by search
      if (search) {
        const brandInfo = card.brandName + card.description;
        if (!textIsInSearch(brandInfo)) {
          return false;
        }
      }
      return true;
    });

    // Separate popular and regular cards
    const popularCards = filteredCards.filter((card) => card.popularity);
    const regularCards = filteredCards.filter((card) => !card.popularity);

    // Sort popular cards by popularity
    const sortedPopularCards = popularCards.sort(
      (a: any, b: any) => a.popularity - b.popularity,
    );

    // Combine cards: popular first (if enabled), then regular
    const cardsToRender = popularCardsAtTop
      ? [...sortedPopularCards, ...regularCards]
      : filteredCards;
    return cardsToRender.map((card, index) => (
      //yeah some lazy keying but we dont do updates
      <RenderCard key={card.brandKey + index} card={card} index={index} />
    ));
  };

  return (
    <div data-cypress="shop-gift-cards-page">
      <Accordion
        data-cypress="gift-cards-filter-accordion"
        sx={{
          margin: "auto",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          pt: 1,
          pb: 1,
          zIndex: 1000,
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          maxWidth: 600,
          display: { xs: "inherit", sm: "none" },
        }}
        expanded={accordionOpen}
      >
        <AccordionSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
          onClick={() => toggleAccordion()}
          sx={{
            backgroundColor: (theme) =>
              category !== "all" || search !== ""
                ? lighten(theme.palette.primary.main, 0.8)
                : "#f8f8f8",
          }}
        >
          <Typography>Filters</Typography>
          <FilterListIcon
            sx={{ lineHeight: 1, margin: "auto 2px", verticalAlign: "middle" }}
            fontSize="small"
          />
        </AccordionSummary>

        <AccordionDetails sx={{ display: { sm: "flex" } }}>
          <FormControl
            sx={{
              marginRight: { xs: 0, sm: 1 },
              width: { xs: "100%", sm: 260, lg: 270 },
            }}
            data-cypress="gift-cards-category-select-mobile"
          >
            <InputLabel id="select-category">Category</InputLabel>
            <Select
              labelId="select-category"
              id="select-category-mobile"
              value={category}
              label="Category"
              onChange={handleChangeCategory}
            >
              <MenuItem value={BRAND_CATEGORIES_ALL}>All</MenuItem>
              {categoryList.map(([catKey, value]) => (
                <MenuItem key={catKey} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            sx={{
              marginTop: { xs: 1, sm: 0 },
              width: { xs: "100%", sm: 260, lg: 270 },
            }}
            variant="outlined"
            name="brandSearch"
            label="Brand Search"
            id="brand-search-mobile"
            onChange={(e) => setSearch(e.target.value)}
            value={search}
            data-cypress="gift-cards-search-input-mobile"
          />
        </AccordionDetails>
      </Accordion>
      <Grid
        container
        alignItems="center"
        margin="auto"
        columns={{ sm: 6 }}
        sx={{
          margin: "auto",
          position: "fixed",
          top: "75px",
          padding: "10px",
          left: 0,
          right: 0,
          zIndex: 1000,
          textAlign: { sm: "center", md: "left" },
          display: { xs: "none", sm: "inherit" },
          width: { sm: 560, md: 840, lg: 1140 },
          backgroundColor: "transparent",
        }}
      >
        <Grid
          size={{ xs: 6 }}
          sx={{
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#f8f8f8",
            padding: "10px",
          }}
        >
          <FormControl
            sx={{
              textAlign: "left",
              marginRight: { xs: 0, sm: 1 },
              width: { xs: "100%", sm: 250, md: 280 },
              backgroundColor: "#f8f8f8",
            }}
            data-cypress="gift-cards-category-select"
          >
            <InputLabel id="select-category">Category</InputLabel>
            <Select
              labelId="select-category"
              id="select-category"
              value={category}
              label="Category"
              onChange={handleChangeCategory}
            >
              <MenuItem value={BRAND_CATEGORIES_ALL}>All</MenuItem>
              {categoryList.map(([catKey, value]) => (
                <MenuItem key={catKey} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            sx={{
              marginTop: { xs: 1, sm: 0 },
              width: { xs: "100%", sm: 250, md: 280 },
            }}
            variant="outlined"
            name="brandSearch"
            label="Brand Search"
            id="brand-search"
            onChange={(e) => setSearch(e.target.value)}
            value={search}
            inputProps={{ "data-cypress": "gift-cards-search-input" }}
          />
        </Grid>
        <Grid size={{ sm: 0, md: 3 }} sx={{ backgroundColor: "transparent" }} />
      </Grid>

      <Grid
        data-cypress="gift-cards-grid"
        container
        alignItems="center"
        margin="auto"
        columns={{ xs: 3, sm: 6, md: 9 }}
        sx={{
          marginTop: { xs: accordionOpen ? "218px" : "68px", sm: "162px" },
          width: { xs: "auto", sm: 560, md: 840, lg: 1140 },
        }}
      >
        {renderCards()}
      </Grid>
    </div>
  );
};

const RenderCard = ({ card, index }: { card: TangoBrand; index: number }) => {
  return (
    <Grid key={card.brandKey} size={3} data-cypress={`gift-card-${index}`}>
      <Link to={`/shop/giftCards/${card.brandKey}`}>
        <Paper
          data-cypress={`gift-card-${card.brandKey}`}
          elevation={2}
          sx={{
            width: { xs: "auto", sm: 260, lg: 360 },
            margin: { xs: "5px 5px", sm: "10px" },
            padding: { xs: "5px 5px", sm: "20px" },
            textAlign: { xs: "left", sm: "center" },
            display: "flex",
            flexDirection: { xs: "row", sm: "column" },
            alignItems: "center",
            position: "relative",
            transition:
              "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
            "&:hover": {
              transform: "scale(1.03)",
              boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          <Box
            sx={{
              flex: { xs: "0 0 auto", sm: "1 0 auto" },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CardImage
              imageUrl={card.imageUrls["300w-326ppi"]}
              margin={{ xs: "5px 5px 0 3px", sm: "auto" }}
              width={{ xs: "112px", sm: "200px", lg: "300px" }}
              height={{ xs: "70.555px", sm: "126px", lg: "190px" }}
            />
          </Box>
          <Box
            sx={{
              flex: { xs: 1, sm: "inherit" },
              paddingLeft: { xs: 2, sm: 0 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              overflow: "hidden",
              marginTop: { sm: 2 },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: 16, lg: 18 },
                fontWeight: "bold",
                width: "100%",
                display: "-webkit-box",
                overflow: "hidden",
                textOverflow: "ellipsis",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                textAlign: { xs: "left", sm: "center" },
              }}
            >
              {card.brandName}
            </Typography>
          </Box>
        </Paper>
      </Link>
    </Grid>
  );
};
